import React, { useState, useEffect, useCallback } from 'react';
import { apiJson } from '../api';
import { colors, theme } from '../styles';

export default function History({ account }) {
  const [sessions, setSessions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [actions, setActions] = useState([]);
  const [limit, setLimit] = useState(50);

  const load = useCallback(async () => {
    let url = `/sessions?limit=${limit}`;
    if (account) url += `&account_id=${account.id}`;
    const data = await apiJson(url);
    if (data) setSessions(data.sessions);
  }, [account, limit]);

  useEffect(() => { load(); }, [load]);

  const toggleSession = async (sessionId) => {
    if (expanded === sessionId) {
      setExpanded(null);
      setActions([]);
      return;
    }
    setExpanded(sessionId);
    const data = await apiJson(`/sessions/${sessionId}/actions`);
    if (data) setActions(data.actions);
  };

  const fmtTime = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const duration = (start, end) => {
    if (!start || !end) return '-';
    const s = (new Date(end) - new Date(start)) / 1000;
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${(s / 3600).toFixed(1)}h`;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#fff', fontSize: 20, margin: 0 }}>
          Session History {account ? `: @${account.username}` : '(All Accounts)'}
        </h2>
        <div style={{ display: 'flex', gap: 4 }}>
          {[25, 50, 100].map(n => (
            <button key={n} style={{
              ...theme.btnSmall,
              background: limit === n ? colors.accent : colors.bgInput,
              color: limit === n ? '#fff' : colors.textDim,
              border: `1px solid ${limit === n ? colors.accent : colors.borderLight}`,
            }} onClick={() => setLimit(n)}>{n}</button>
          ))}
          <button style={theme.btnSecondary} onClick={load}>Refresh</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sessions.map(s => {
          const isExpanded = expanded === s.id;
          const hasErrors = s.total_errors > 0;
          const statusColor = s.status === 'completed' ? colors.green :
                              s.status === 'failed' ? colors.red :
                              s.status === 'running' ? colors.accent : colors.orange;

          return (
            <div key={s.id}>
              {/* Session summary row */}
              <div
                onClick={() => toggleSession(s.id)}
                style={{
                  ...theme.card, padding: '10px 16px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12,
                  borderColor: isExpanded ? colors.accent : hasErrors ? colors.red + '44' : colors.border,
                  borderLeftWidth: 3, borderLeftColor: statusColor,
                }}
              >
                {/* Error indicator */}
                {hasErrors && (
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.red, flexShrink: 0 }} />
                )}

                {/* Account */}
                {!account && (
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, minWidth: 100 }}>@{s.username}</span>
                )}

                {/* Time */}
                <span style={{ color: colors.textDim, fontSize: 12, minWidth: 100 }}>{fmtTime(s.started_at)}</span>
                <span style={{ color: colors.textDimmer, fontSize: 11 }}>{duration(s.started_at, s.ended_at)}</span>

                {/* Action counts */}
                <div style={{ display: 'flex', gap: 12, flex: 1, justifyContent: 'center' }}>
                  <StatPill label="L" value={s.total_likes} color={colors.red} />
                  <StatPill label="C" value={s.total_comments} color={colors.blue} />
                  <StatPill label="F" value={s.total_follows} color={colors.green} />
                  <StatPill label="U" value={s.total_unfollows} color={colors.orange} />
                  {hasErrors && <StatPill label="E" value={s.total_errors} color={colors.red} />}
                </div>

                {/* Status badge */}
                <span style={theme.badge(statusColor)}>{s.status}</span>

                {/* Expand icon */}
                <span style={{ color: colors.textDim, fontSize: 14, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                  &#9654;
                </span>
              </div>

              {/* Expanded actions */}
              {isExpanded && (
                <div style={{
                  background: colors.bgDark, borderRadius: '0 0 8px 8px',
                  border: `1px solid ${colors.accent}44`, borderTop: 'none',
                  padding: '8px 16px', maxHeight: 400, overflow: 'auto',
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: 'monospace' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>Action</th>
                        <th style={thStyle}>Detail</th>
                        <th style={thStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actions.map(a => (
                        <tr key={a.id} style={{ borderBottom: `1px solid ${colors.border}11` }}>
                          <td style={{ ...tdStyle, color: colors.textDimmer, whiteSpace: 'nowrap' }}>
                            {new Date(a.timestamp).toLocaleTimeString()}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ color: actionColor(a.action_type), fontWeight: 500 }}>
                              {a.action_type}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, color: colors.textMuted, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {a.detail || '-'}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              color: a.status === 'error' ? colors.red :
                                     a.status === 'warning' ? colors.orange :
                                     a.status === 'info' ? colors.blue : colors.green,
                              fontSize: 10,
                            }}>{a.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {actions.length === 0 && (
                    <div style={{ textAlign: 'center', color: colors.textDim, padding: 16 }}>No actions recorded.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {sessions.length === 0 && (
          <div style={{ ...theme.card, textAlign: 'center', color: colors.textDim, padding: 32 }}>
            No sessions recorded yet. Start an automation to see session history.
          </div>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontSize: 10, color: colors.textDimmer }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: value > 0 ? color : colors.textDimmer }}>{value || 0}</span>
    </span>
  );
}

function actionColor(type) {
  const map = { like: colors.red, comment: colors.blue, follow: colors.green, unfollow: colors.orange,
    search: colors.accent, navigate: colors.accent, error: colors.red, fatal_error: colors.red,
    session_start: colors.green, session_complete: colors.green };
  return map[type] || colors.textMuted;
}

const thStyle = { padding: '6px', textAlign: 'left', fontSize: 10, color: colors.textDim, textTransform: 'uppercase' };
const tdStyle = { padding: '4px 6px', fontSize: 11 };
